/* Toshi standalone size-finder — REFERENCE MODE.
 *
 * Powers the <size-finder-ref> element on the size-chart pages
 * (sections/toshi-size-finder-chart-page.liquid). Unlike the PDP finder
 * (toshi-pdp-size-finder.js) there is NO product in scope: no variant radios,
 * no add-to-cart, no stock. The job is purely advisory — pick an age, get a
 * recommended size plus a size-up / size-down alternative, each with its
 * measurement.
 *
 * DATA FLOW: unlike the PDP — which reads a live product metafield
 * (Airtable ➜ sync ➜ custom.size_finder ➜ [data-sf-data]) — these pages have no
 * product, so the section carries a HAND-BAKED snapshot of the same Airtable
 * rows. It does not track Airtable edits; re-bake it by hand.
 *
 * Data: the same flat Airtable rollup rows the custom.size_finder metafield
 * carries (one row per size: sku_size_code / size_name / age_low / age_high /
 * age_band_period / measure_primary / measure_secondary / sku_sort_order).
 * Measures are label-first strings ("height 84 cm", "chest 52 cm"). The row
 * contract and its label/unit/order derivation live in the SHARED data layer,
 * assets/toshi-size-finder-data.js (window.ToshiSizeFinderData), also
 * consumed by toshi-pdp-size-finder.js — the section must load it (defer)
 * BEFORE this file. A row with an age band is both a size and its own
 * discrete age chip, so the recommendation is direct — chip → its row — and
 * no head-circumference statistics are needed here. A CHIPLESS row (blank
 * age_low, e.g. headwear XL — a larger-head fit variant of 13+) keeps its
 * series position as an alternate card and chart-table row but gets no chip,
 * so chips map to row indices via `chipRows` rather than 1:1.
 *
 * Layout: one HERO card ("Most common: size X") flanked by smaller
 * size-down / size-up alternatives. To keep the look identical to the PDP
 * finder, this renders the PDP's own markup + classes (.sf-ages/.sf-chip for
 * the age row; .sf-sizes/.sf-size/.sf-spec/.sf-sk/.sf-scm/.sf-banner for the
 * cards; .sf-howrow/.sf-tipinline/.sf-tabs for the expandable "Size chart"
 * card) so BOTH finders share one ruleset in assets/toshi-custom.css. There
 * are no chart-page-specific card styles — if you restyle the PDP cards, this
 * page follows automatically. (The PDP-only bits — stock/OOS banners, variant
 * radios, add-to-cart — simply aren't emitted here.)
 *
 * SELECTION behaviour matches the PDP (shared SizeSelection — see
 * connectedCallback): the hero is always the age's most-common size, clicking
 * an alt SELECTS it (✓ + chart-row highlight) without moving the hero. It
 * differs from the PDP in one deliberate way: this page defaults to the
 * youngest age with the cards already expanded and the most-common pre-ticked,
 * because a reference page should be useful on load rather than blank. The
 * selection logic lives in the shared data layer; this element owns only its
 * age-to-recommendation mapping and advisory rendering.
 *
 * The expandable card has the SAME two tabs as the PDP: Size Chart (built by
 * the shared buildChartTable, row highlighted via markChartRow) and How to
 * measure (baked copy + diagram from the snippet's part:'measure'). Unlike the
 * PDP the measure content is page-level (baked per category), not per-product.
 */
(() => {
  // shared data layer — row→display parse, splitMeasure (cards stack the
  // measure descriptor-over-value like the PDP), and buildChartTable +
  // markChartRow (the expandable chart table + its selection highlight, the
  // SAME functions the PDP uses).
  const {
    parseRows, buildChartTable, markChartRow, createSizeSelection,
    cardInnerHtml, emptyCardInnerHtml, selectTab, isDevSurface,
  } = window.ToshiSizeFinderData;

  class SizeFinderRef extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dataEl = this.querySelector('[data-sf-data]');
      if (!dataEl) return;
      let raw;
      try {
        raw = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      this.rows = parseRows(Array.isArray(raw) ? raw : [], this.dataset.sfSizeDisplay);
      if (this.rows.length < 2) {
        // missing/empty chart reference (the payload is null when the page has
        // no custom.size_chart_content): the section stays empty for shoppers
        // — never a default chart. Dev surfaces get the Liquid-rendered
        // warning instead of a silently blank page.
        const devWarn = this.querySelector('[data-sf-devwarn]');
        if (devWarn && isDevSurface()) {
          devWarn.hidden = false;
        } else {
          this.hidden = true;
        }
        return;
      }

      this.ages = this.querySelector('[data-sf-ages]');
      this.result = this.querySelector('[data-sf-result]');
      if (!this.ages || !this.result) return;

      // expandable "Size chart" card (PDP pattern): a toggle row + a hidden
      // panel holding the data-built chart table. Optional — the finder still
      // works without them.
      this.tipRow = this.querySelector('[data-sf-howrow]');
      this.tipBtn = this.querySelector('[data-sf-tip-btn]');
      this.tipPop = this.querySelector('[data-sf-tip]');
      this.chartEl = this.querySelector('[data-sf-chart]');

      // chips come only from rows with an age band; chipRows maps chip
      // position → row index (selection state is in ROW indices throughout,
      // so chipless rows still work as alternate cards / table rows)
      this.chipRows = this.rows
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => r.hasBand)
        .map(({ i }) => i);

      // A reference page starts useful: youngest age selected, hero ticked.
      // Unlike PDP deselection, deselecting an explicit chart pick re-ticks its
      // age's hero rather than returning to an unticked hint.
      // (First CHIP's row, not row 0 — row 0 could in principle be chipless.)
      this.selection = createSizeSelection({
        recommendedIdx: this.chipRows.length ? this.chipRows[0] : 0,
        confirmed: true,
        retainConfirmationOnDeselect: true,
      });
      this.hydrateAgeChips();
      // build the chart table once from the same rows the cards use, so the
      // expandable chart and the summary cards can never drift
      buildChartTable(this.chartEl,
        this.rows.map((r) => ({ size: r.size, primary: r.measure, secondary: r.secondary })));
      this.bindKeyboard();
      this.bindTip();
      this.render();

      // "Open size chart by default" (section setting → data-sf-open-chart):
      // a reference page's chart is the destination, so it starts expanded on
      // the Chart tab — still collapsible via the toggle. PDP-only behaviour
      // is untouched (its finder never sets this attribute).
      if (this.hasAttribute('data-sf-open-chart') && this.tipBtn && this.tipPop) {
        this.tipPop.hidden = false;
        this.tipBtn.setAttribute('aria-expanded', 'true');
        this.selectTab('chart');
      }
    }

    // selecting an age moves the window AND confirms the most-common (PDP
    // auto-confirm on age select); any prior explicit pick is dropped
    selectAge(i) {
      this.selection.chooseRecommendation(i);
      this.render();
    }

    hydrateAgeChips() {
      this.ages.innerHTML = '';
      // one chip per BANDED row (chipRows); a chipless row gets no chip
      this.chipRows.forEach((rowIdx) => {
        const r = this.rows[rowIdx];
        const b = document.createElement('button');
        // PDP classes (.sf-chip / .sf-cv / .sf-cu) so the age row shares the
        // PDP finder's ruleset in toshi-custom.css — one source of truth.
        b.className = 'sf-chip';
        b.type = 'button';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', 'false');
        b.setAttribute('aria-label', `${r.label} ${r.unit}`);
        b.innerHTML = `<span class="sf-cv">${r.label}</span><span class="sf-cu">${r.unit}</span>`;
        b.addEventListener('click', () => this.selectAge(rowIdx));
        this.ages.appendChild(b);
      });
    }

    render() {
      // the age chip reflects the most-common (window) row, not the pick —
      // clicking an alt size doesn't change which age is selected. Chips are
      // positional; chipRows maps each back to its row index.
      [...this.ages.children].forEach((b, ci) => {
        b.setAttribute('aria-checked', this.chipRows[ci] === this.selection.recommendedIdx ? 'true' : 'false');
      });

      // window is anchored to the age (mostI); the hero is always the
      // most-common. down/up are its neighbours (an edge renders empty).
      const { downIdx, mostIdx: most, upIdx } = this.selection.window(this.rows.length);
      const cur = this.selection.currentIdx();
      const rec = this.rows[most];
      const down = downIdx != null ? this.rows[downIdx] : null;
      const up = upIdx != null ? this.rows[upIdx] : null;

      /* Card inner markup comes from the SHARED cardInnerHtml (same function
         the PDP uses): "size" label over the code, primary-measure
         descriptor-over-value. The aria-label announces the full measure
         (primary + any secondary) for assistive tech. */
      const fullMeasure = (r) => (r.secondary ? `${r.measure}, ${r.secondary}` : r.measure);

      // one row: size-down · most-common (primary, wider) · size-up. A missing
      // edge (first/last size) renders a struck-through empty card (shared
      // emptyCardInnerHtml, sized against the hero's measure) so the row always
      // reads as three cards and the hero stays centred.
      const emptyCard = () => `<div class="sf-size sf-size--empty" aria-hidden="true">${
        emptyCardInnerHtml(rec.measure)}</div>`;

      // a card shows the ✓ only when it's the confirmed current selection.
      // The hero always wears the "Most common" banner regardless of ✓; the
      // hero stays the hero even when an alt is the confirmed pick (the alt
      // gets the ✓, the hero keeps its banner) — this is the key PDP behaviour.
      const isSel = (idx) => this.selection.confirmed && idx === cur;

      const heroCard = () =>
        `<button type="button" class="sf-size sf-size--primary" role="radio" aria-checked="${isSel(most)}"
           aria-label="Size ${rec.size}, ${fullMeasure(rec)}, most common for ${rec.label} ${rec.unit}">
          <span class="sf-banner sf-banner--top">Most common</span>
          ${cardInnerHtml(rec.size, rec.measure)}
        </button>`;

      const altCard = (r, idx, kind, announce) => {
        if (!r) return emptyCard();
        return `<button type="button" class="sf-size sf-size--${kind}" role="radio" aria-checked="${isSel(idx)}"
                  aria-label="Size ${r.size}, ${fullMeasure(r)}, ${announce}">
            ${cardInnerHtml(r.size, r.measure)}
          </button>`;
      };

      this.result.innerHTML =
        altCard(down, most - 1, 'down', 'size down')
        + heroCard()
        + altCard(up, most + 1, 'up', 'size up');
      this.result.hidden = false;

      // clicking a card SELECTS that size (ticks it + moves the chart-row
      // highlight) without moving the hero. Clicking the already-ticked card
      // deselects, dropping back to the most-common as the current size. Mirrors
      // the PDP's card-click toggle.
      this.result.querySelectorAll('.sf-size--down, .sf-size--primary, .sf-size--up').forEach((el) => {
        const idx = el.classList.contains('sf-size--down') ? downIdx
          : el.classList.contains('sf-size--up') ? upIdx : most;
        el.addEventListener('click', () => {
          this.selection.toggleExplicit(idx);
          this.render();
        });
      });

      // offer the "Size chart" toggle and, if the chart is open, highlight the
      // row for the current selection (handoff goal #2)
      if (this.tipRow) this.tipRow.hidden = false;
      markChartRow(this.chartEl, cur);
    }

    bindKeyboard() {
      this.ages.addEventListener('keydown', (e) => {
        const MAX = this.chipRows.length - 1;
        // arrows move the selected AGE chip (radiogroup pattern), starting
        // from the chip of the current most-common row — chip positions, not
        // row indices, so a chipless row is simply never a stop
        const go = (ci) => {
          this.selectAge(this.chipRows[ci]);
          this.ages.children[ci].focus();
          e.preventDefault();
        };
        const curChip = Math.max(0, this.chipRows.indexOf(this.selection.recommendedIdx));
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') go(Math.min(MAX, curChip + 1));
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') go(Math.max(0, curChip - 1));
        else if (e.key === 'Home') go(0);
        else if (e.key === 'End') go(MAX);
      });
    }

    /* collapse the expandable "Size chart" panel — used by Escape and when the
       finder hides the toggle row on an age toggle-off. No-op if absent/closed. */
    closeTip() {
      if (!this.tipPop || this.tipPop.hidden) return;
      this.tipPop.hidden = true;
      this.tipBtn?.setAttribute('aria-expanded', 'false');
    }

    /* switch the tip card's tabs (Chart / How to measure) — the SHARED
       selectTab, keyed by data-sf-tab ↔ data-sf-panel (same as the PDP). */
    selectTab(name) {
      selectTab(this, name);
    }

    /* the "Size chart" toggle: show/hide the two-tab panel (Size Chart + How to
       measure), landing on the Chart tab — matching the PDP. Escape closes it
       and returns focus to the trigger. */
    bindTip() {
      if (!this.tipBtn || !this.tipPop) return;
      this.tipBtn.addEventListener('click', () => {
        const open = this.tipPop.hidden;
        this.tipPop.hidden = !open;
        this.tipBtn.setAttribute('aria-expanded', String(open));
        if (open) this.selectTab('chart');
      });
      this.querySelectorAll('[data-sf-tab]').forEach((t) => {
        t.addEventListener('click', () => this.selectTab(t.dataset.sfTab));
      });
      this.tipPop.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || this.tipPop.hidden) return;
        this.closeTip();
        this.tipBtn.focus();
      });
    }
  }

  if (!window.customElements.get('size-finder-ref')) {
    window.customElements.define('size-finder-ref', SizeFinderRef);
  }
})();
